import type * as React from 'react'

import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useIsTruncated } from '@/hooks/use-is-truncated'
import { cn } from '@/lib/utils'
import type { AnswerTone, RubricView } from '@/features/calls/rubricView'

/**
 * One rubric's result in the Evaluation Rubric Results panel. Every rubric
 * type renders through the same layout; `rubricView` decides what goes in it.
 */

const TONE_VARIANT = {
  primary: 'default',
  error: 'destructive',
  neutral: 'secondary',
} as const satisfies Record<AnswerTone, string>

const NEUTRAL = 'bg-neutral-subtle-bg text-muted-foreground'

/**
 * A result pill that truncates a long label (level names run to 100
 * characters). Its tooltip shows the full label and any extra detail, and
 * the pill takes focus whenever there is a tooltip, so it isn't hover-only.
 */
function Pill({
  text,
  tooltip,
  variant,
  className,
  children,
}: {
  text: string
  tooltip?: string
  variant: React.ComponentProps<typeof Badge>['variant']
  className?: string
  children?: React.ReactNode
}) {
  const { ref, isTruncated } = useIsTruncated<HTMLSpanElement>()
  const hasTooltip = Boolean(tooltip) || isTruncated
  return (
    <Tooltip>
      <TooltipTrigger disabled={!hasTooltip} asChild>
        <Badge
          variant={variant}
          tabIndex={hasTooltip ? 0 : undefined}
          // `disabled` doesn't stop Radix opening on an asChild span, so a pill
          // with nothing to show opts out of hover, as CallLabelBadge does.
          className={cn(
            'max-w-full',
            !hasTooltip && 'pointer-events-none',
            className
          )}>
          <span ref={ref} className="truncate">
            {text}
          </span>
          {children}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        {isTruncated && <p>{text}</p>}
        {tooltip && <p>{tooltip}</p>}
      </TooltipContent>
    </Tooltip>
  )
}

/** The one-glance answer pill(s), right-aligned beside the title. */
export function RubricHeadline({ view }: { view: RubricView }) {
  return (
    <div className="flex max-w-[50%] min-w-0 shrink-0 flex-wrap justify-end gap-1">
      {view.answers.map((answer) => (
        <Pill
          key={answer.text}
          text={answer.text}
          tooltip={answer.tooltip}
          variant={TONE_VARIANT[answer.tone]}
          className={answer.tone === 'neutral' ? NEUTRAL : undefined}
        />
      ))}
    </div>
  )
}

export function RubricDetail({ view }: { view: RubricView }) {
  return (
    <>
      {(view.best.length > 0 || view.others.length > 0) && (
        <div className="space-y-0.5">
          {view.best.length > 0 && (
            <p className="text-muted-foreground text-xs">
              {view.best.length > 1 ? 'Tied best match: ' : 'Best match: '}
              <span className="text-primary font-medium">
                {view.best.join(', ')}
              </span>
            </p>
          )}
          {view.others.length > 0 && (
            <p className="text-muted-foreground/70 text-xs">
              {view.others.join(' · ')}
            </p>
          )}
        </div>
      )}
      {view.chips.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {view.chips.map((chip) => (
            <li key={chip.label} className="max-w-full">
              <Pill text={chip.label} tooltip={chip.tooltip} variant="default">
                {chip.detail !== null && (
                  <span className="font-normal opacity-70">{chip.detail}</span>
                )}
              </Pill>
            </li>
          ))}
        </ul>
      )}
      {view.note && (
        <p className="text-muted-foreground text-xs leading-relaxed break-words whitespace-pre-wrap">
          {view.note}
        </p>
      )}
    </>
  )
}
